import { authGuard, permissionGuard } from '@abp/ng.core';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { TenantGuard, TenantGuardHost } from './guards/tenant.guard';
import {
  aceitasRecusadasGuard,
  confirmacaoEntregasGuard,
  fleetScoreGuard,
  bolsaoGuard,
  flowPayGuard,
  gerenciamentoDeViagemGuard,
  gerenciamentoViagensNovoGuard,
  leilaoGuard,
  rotasGuard,
} from './guards/feature.guard';
import { tenantManagementEntityActionContributors } from './entity-action-contributors';
import { activationStatePropContributors } from './tenants/extensions/tenants-entity-props';
import { endDateStatePropContributors } from './tenants/extensions/tenant-form-props'
import { endDateEditPropsContributors } from './tenants/extensions/tenant-form-edit-props';

const routes: Routes = [
  /*
  {
    path: '',
    pathMatch: 'full',
    loadChildren: () => import('./home/home.module').then(m => m.HomeModule),
  },
  */
  {
    path: '',
    loadChildren: () =>
      import('@volo/abp.ng.saas').then(m =>
        m.SaasModule.forLazy({
          entityPropContributors: activationStatePropContributors,
          entityActionContributors: tenantManagementEntityActionContributors,
          createFormPropContributors: endDateStatePropContributors,
          editFormPropContributors: endDateEditPropsContributors,
        }),
      ), canActivate: [authGuard, permissionGuard],
    canMatch: [TenantGuard],
  },
  {
    // Destino da raiz decidido em runtime: /ofertas-abertas fixo virava loop para tenant sem o
    // módulo Leilão (o guard devolve para a raiz). Ver HomeRedirectComponent.
    path: '',
    pathMatch: 'full',
    canMatch: [authGuard, permissionGuard],
    loadComponent: () => import('./home-redirect.component').then(m => m.HomeRedirectComponent),
  },

  {
    path: 'dashboard',
    loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
    canActivate: [authGuard, permissionGuard, leilaoGuard],
  },

  {
    path: 'metricas-dashboard',
    loadChildren: () => import('./metricas-dashboard/metricas-dashboard.module').then(m => m.MetricasDashboardModule),
    canActivate: [authGuard, permissionGuard],
  },

  {
    path: 'clientes',
    loadChildren: () => import('./clientes/clientes.module').then(m => m.ClientesModule),
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'cargo_fleet.Clientes.Host' },
  },

  {
    path: 'radar-acessos',
    loadChildren: () => import('./radar-acessos/radar-acessos.module').then(m => m.RadarAcessosModule),
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'cargo_fleet.RadarAcessos.Host' },
  },

  {
    path: 'account',
    loadChildren: () =>
      import('@volo/abp.ng.account/public').then(m => m.AccountPublicModule.forLazy()),
  },
  {
    path: 'gdpr',
    loadChildren: () => import('@volo/abp.ng.gdpr').then(m => m.GdprModule.forLazy()),
  },
  {
    path: 'identity',
    loadChildren: () => import('@volo/abp.ng.identity').then(m => m.IdentityModule.forLazy()),
  },
  {
    path: 'language-management',
    loadChildren: () =>
      import('@volo/abp.ng.language-management').then(m => m.LanguageManagementModule.forLazy()),
  },

  {
    path: 'audit-logs',
    loadChildren: () =>
      import('@volo/abp.ng.audit-logging').then(m => m.AuditLoggingModule.forLazy()),
  },
  {
    path: 'openiddict',
    loadChildren: () =>
      import('@volo/abp.ng.openiddictpro').then(m => m.OpeniddictproModule.forLazy()),
  },
  {
    path: 'text-template-management',
    loadChildren: () =>
      import('@volo/abp.ng.text-template-management').then(m =>
        m.TextTemplateManagementModule.forLazy(),
      ),
  },
  {
    path: 'setting-management',
    loadChildren: () =>
      import('@abp/ng.setting-management').then(m => m.SettingManagementModule.forLazy()),
  },
  {
    path: 'gdpr-cookie-consent',
    loadChildren: () =>
      import('./gdpr-cookie-consent/gdpr-cookie-consent.module').then(
        m => m.GdprCookieConsentModule,
      ),
  },
  {
    path: 'rotas',
    loadChildren: () => import('./rotas/rota/rota.module').then(m => m.RotaModule),
    canActivate: [authGuard, rotasGuard],
  },
  {
    path: 'ofertas',
    loadChildren: () => import('./ofertas/oferta/oferta.module').then(m => m.OfertaModule),
    canActivate: [authGuard, aceitasRecusadasGuard],
  },
  {
    path: 'ofertas-abertas',
    loadChildren: () =>
      import('./ofertas-abertas/ofertas-abertas.module').then(m => m.OfertasAbertasModule),
    canActivate: [authGuard, leilaoGuard],
  },
  {
    path: 'saas',
    loadChildren: () =>
      import('@volo/abp.ng.saas').then(m =>
        m.SaasModule.forLazy({
          entityPropContributors: activationStatePropContributors,
          entityActionContributors: tenantManagementEntityActionContributors,
          createFormPropContributors: endDateStatePropContributors,
          editFormPropContributors: endDateEditPropsContributors,
        }),
      ),
    canActivate: [authGuard, permissionGuard],
    canMatch: [TenantGuardHost],
  },
  {
    path: 'oferta-records',
    loadChildren: () =>
      import('./oferta-records/oferta-record/oferta-record.module').then(m => m.OfertaRecordModule),
    canActivate: [authGuard, aceitasRecusadasGuard],
  },
  {
    path: 'api-status',
    loadChildren: () =>
      import('./api-statuses/api-status/api-status.module').then(m => m.ApiStatusModule),
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'cargo_fleet.ApiStatuses' },
  },
  {
    path: 'galileu-saidas',
    loadChildren: () =>
      import('./galileu-saidas/galileu-saidas.module').then(m => m.GalileuSaidasModule),
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'cargo_fleet.GalileuSaidas' },
  },
  {
    path: 'curadoria-ofertas',
    loadChildren: () =>
      import('./curadoria-ofertas/curadoria-ofertas.module').then(m => m.CuradoriaOfertasModule),
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'cargo_fleet.CuradoriaOfertas.Host' },
  },
  {
    path: 'whats-app-contacts',
    loadChildren: () =>
      import('./whats-app-contacts/whats-app-contact/whats-app-contact.module').then(
        m => m.WhatsAppContactModule,
      ),
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'cargo_fleet.WhatsAppContacts' },
  },
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent),
  },
  {
    path: 'viagens',
    loadChildren: () => import('./viagens/viagens.module').then(m => m.ViagemModule),
    canActivate: [authGuard, permissionGuard, gerenciamentoDeViagemGuard],
    data: { requiredPolicy: 'cargo_fleet.Viagens' },
  },
  {
    path: 'gerenciamento-viagens',
    loadChildren: () =>
      import('./gerenciamento-viagens/gerenciamento-viagens.module').then(
        m => m.GerenciamentoViagensModule,
      ),
    canActivate: [authGuard, permissionGuard, gerenciamentoViagensNovoGuard],
    data: { requiredPolicy: 'cargo_fleet.GerenciamentoViagens' },
  },
  {
    path: 'faturamento',
    loadComponent: () =>
      import('./faturamento/faturamento-redirect.component').then(
        m => m.FaturamentoRedirectComponent,
      ),
    canActivate: [authGuard],
    pathMatch: 'full',
  },
  {
    path: 'faturamento/flowpay',
    loadChildren: () => import('./flowpay/flowpay.module').then(m => m.FlowPayModule),
    canActivate: [authGuard, permissionGuard, flowPayGuard],
    data: { requiredPolicy: 'cargo_fleet.FlowPay' },
  },
  {
    path: 'bolsao',
    loadChildren: () => import('./bolsao/bolsao.module').then(m => m.BolsaoModule),
    canActivate: [authGuard, bolsaoGuard],
  },
  {
    path: 'fleet-score',
    loadChildren: () => import('./fleet-score/fleet-score.module').then(m => m.FleetScoreModule),
    canActivate: [authGuard, permissionGuard, fleetScoreGuard],
    data: { requiredPolicy: 'cargo_fleet.FleetScore' },
  },
  {
    path: 'faturamento/confirmacao-entregas',
    loadChildren: () =>
      import('./confirmacao-entregas/confirmacao-entregas.module').then(
        m => m.ConfirmacaoEntregasModule,
      ),
    canActivate: [authGuard, permissionGuard, confirmacaoEntregasGuard],
    data: { requiredPolicy: 'cargo_fleet.ConfirmacaoEntregas' },
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
